import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/pro-regular-svg-icons'
import {
  ModalConfirmV2,
  ModalSizeVariants,
  TextInputV4,
} from '@labrav/react-components'
import { useStyles } from './UpliftPercentageModal.styles'
import { parsePercentInput } from '../../utils/renewalDetailsFormat'
import {
  UPLIFT_PERCENTAGE_MODAL_DESCRIPTION,
  UPLIFT_PERCENTAGE_RANGE_HELPER,
  UPLIFT_PERCENTAGE_REQUIRED,
} from '../../utils/messagesContants'

export type UpliftPercentageModalProps = {
  open: boolean
  minPercent?: number | null
  maxPercent?: number | null
  initialPercent?: number | null
  onClose: () => void
  onSubmit: (percent: number) => void
}

export const UpliftPercentageModal = ({
  open,
  minPercent,
  maxPercent,
  initialPercent,
  onClose,
  onSubmit,
}: UpliftPercentageModalProps) => {
  const { classes } = useStyles()
  const [value, setValue] = useState('')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    setValue(
      initialPercent != null && Number.isFinite(Number(initialPercent))
        ? String(initialPercent)
        : ''
    )
  }, [open, initialPercent])

  const parsed = parsePercentInput(value)
  const min =
    minPercent != null && Number.isFinite(Number(minPercent))
      ? Number(minPercent)
      : null
  const max =
    maxPercent != null && Number.isFinite(Number(maxPercent))
      ? Number(maxPercent)
      : null

  const outOfRange =
    parsed != null &&
    ((min != null && parsed < min) || (max != null && parsed > max))
  const emptyError = touched && parsed == null
  const showError = emptyError || outOfRange
  const canSubmit = parsed != null && !outOfRange

  const handleAccept = () => {
    setTouched(true)
    if (!canSubmit || parsed == null) return
    onSubmit(parsed)
    onClose()
  }

  return (
    <ModalConfirmV2
      open={open}
      onDecline={onClose}
      onAccept={handleAccept}
      variant={ModalSizeVariants.medium}
      title="Uplift percentage"
      titleCancelButton="Back"
      titleAcceptButton="Submit to AWS"
      acceptDisabled={!canSubmit}
      icon={
        <FontAwesomeIcon
          icon={faPen}
          style={{ width: '16px', height: '16px' }}
        />
      }
      content={
        <div data-testid="uplift-percentage-modal">
          <div className={classes.description}>
            {UPLIFT_PERCENTAGE_MODAL_DESCRIPTION}
          </div>
          <div className={classes.inputWrap}>
            <TextInputV4
              keyName="enteredUpliftPercent"
              title="Price uplift percentage"
              placeHolder="Price uplift percentage"
              type="text"
              isRequired
              showSpinner={false}
              value={value === '' ? '' : `${value}%`}
              updatedValue={value === '' ? '' : `${value}%`}
              onChange={(e: { target: { value: string } }) => {
                const raw = e.target.value.replace(/%/g, '').trim()
                if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
                  setValue(raw)
                }
              }}
              isError={showError}
              errorText={
                emptyError
                  ? UPLIFT_PERCENTAGE_REQUIRED
                  : outOfRange
                    ? UPLIFT_PERCENTAGE_RANGE_HELPER
                    : ''
              }
              isReadOnly={false}
            />
          </div>
          {!showError && (
            <div className={classes.helper}>
              {UPLIFT_PERCENTAGE_RANGE_HELPER}
            </div>
          )}
        </div>
      }
    />
  )
}
